import { useSEO } from "@/hooks/useSEO";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Instagram, Youtube } from "lucide-react";
import { useEffect, useRef } from "react";
import profileImage from "@/assets/profile.jpg";
import { BRAND_HANDLE, BRAND_PERSON_NAME } from "@/lib/brandConfig";
import {
  buildTallyContactEmbedDataSrc,
  TALLY_WIDGET_SCRIPT,
} from "@/lib/tallyContactEmbed";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const socialLinks = [
  { icon: Instagram, label: "Instagram", href: `https://instagram.com/${BRAND_HANDLE}` },
  { icon: Youtube, label: "YouTube", href: `https://www.youtube.com/@${BRAND_HANDLE}` },
];

const Contacto = () => {
  useSEO("contacto");
  const { t } = useTranslation();
  const tallyLoadedRef = useRef(false);
  // Id del form Tally editable desde /admin/site-settings; fallback al default.
  const { settings: siteSettings } = useSiteSettings();
  const tallyEmbedSrc = buildTallyContactEmbedDataSrc(siteSettings.tallyContactFormId);

  useEffect(() => {
    const initTallyEmbeds = () => {
      const w = window as Window & { Tally?: { loadEmbeds: () => void } };
      if (w.Tally) {
        w.Tally.loadEmbeds();
        return;
      }
      document.querySelectorAll<HTMLIFrameElement>("iframe[data-tally-src]:not([src])").forEach((el) => {
        const src = el.dataset.tallySrc;
        if (src) el.src = src;
      });
    };

    if (tallyLoadedRef.current) {
      initTallyEmbeds();
      return;
    }

    if (document.querySelector(`script[src="${TALLY_WIDGET_SCRIPT}"]`)) {
      tallyLoadedRef.current = true;
      initTallyEmbeds();
      return;
    }

    const script = document.createElement("script");
    script.src = TALLY_WIDGET_SCRIPT;
    script.async = true;
    script.onload = () => {
      tallyLoadedRef.current = true;
      initTallyEmbeds();
    };
    script.onerror = initTallyEmbeds;
    document.body.appendChild(script);
    // Re-corre si el id del form (site_settings) cambia tras cargar: el <iframe>
    // se remonta (key) sin src y este efecto se lo vuelve a asignar.
  }, [tallyEmbedSrc]);

  return (
    <div className="min-h-screen bg-background">
      <main className="max-w-md mx-auto px-5 py-12 md:py-16">
        <header className="text-center mb-10 animate-fade-in">
          <div className="relative inline-block mb-8">
            <div className="absolute -inset-1 rounded-full bg-gradient-to-br from-border via-muted to-border opacity-60" />
            <div className="relative w-28 h-28 rounded-full overflow-hidden ring-2 ring-background shadow-lg">
              <img
                src={profileImage}
                alt={`${BRAND_PERSON_NAME} - Contact portrait photo`}
                loading="lazy"
                width={112}
                height={112}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-transparent via-background/10 to-transparent pointer-events-none" />
          </div>

          <h1 className="text-3xl md:text-4xl font-display font-medium text-foreground tracking-wide mb-3 italic">
            {t("contactPage.title")}
          </h1>
          <div className="flex items-center justify-center gap-2 mb-5">
            <span className="w-6 h-px bg-border" />
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-medium">
              {t("contactPage.subtitle")}
            </p>
            <span className="w-6 h-px bg-border" />
          </div>
        </header>

        <section className="mb-10 animate-fade-in" style={{ animationDelay: "0.1s" }}>
          <div className="prose prose-sm max-w-none text-muted-foreground space-y-4 text-center">
            <p className="text-sm leading-relaxed">{t("contactPage.p1")}</p>
            <p className="text-sm leading-relaxed">{t("contactPage.p2")}</p>
          </div>
        </section>

        <section
          className="mb-10 w-full animate-fade-in rounded-xl border border-border/60 bg-card/30 overflow-hidden"
          style={{ animationDelay: "0.15s" }}
          aria-label={t("contactPage.title")}
        >
          <iframe
            key={tallyEmbedSrc}
            data-tally-src={tallyEmbedSrc}
            loading="lazy"
            width="100%"
            height={578}
            style={{ minHeight: 400, border: 0, display: "block" }}
            title="Contacto"
            allow="clipboard-write"
          />
        </section>

        <section className="mb-10 animate-fade-in" style={{ animationDelay: "0.2s" }}>
          <div className="flex justify-center gap-6">
            {socialLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-all duration-300"
                aria-label={link.label}
              >
                <link.icon className="w-4 h-4" strokeWidth={1.5} />
              </a>
            ))}
          </div>
        </section>

        <div className="text-center animate-fade-in" style={{ animationDelay: "0.3s" }}>
          <Link
            to="/"
            className="inline-block text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-foreground/20 rounded"
          >
            {t("contactPage.backHome")}
          </Link>
        </div>

        <footer className="mt-14 text-center animate-fade-in" style={{ animationDelay: "0.4s" }}>
          <p className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground/60">
            {t("contactPage.footer")}
          </p>
        </footer>
      </main>
    </div>
  );
};

export default Contacto;
